/**
 * Cloud Logging Initializer Component
 * Initializes cloud logging when the app starts
 */

import { useEffect } from 'react';
import { initializeCloudLogging, logToCloud } from '../services/cloudLoggingService';

interface CloudLoggingInitializerProps {
  children: React.ReactNode;
}

export const CloudLoggingInitializer: React.FC<CloudLoggingInitializerProps> = ({ children }) => {
  useEffect(() => {
    console.log('🔄 CloudLoggingInitializer: Starting initialization...');
    
    // Initialize cloud logging
    const cloudLogging = initializeCloudLogging({
      enabled: true, // Enable in all environments for testing
      batchSize: 5, // Smaller batch for faster debugging
      flushInterval: 3000, // 3 seconds for faster feedback
    });

    console.log('✅ CloudLoggingInitializer: Cloud logging service initialized');
    console.log('📊 Session ID:', cloudLogging.getSessionId());

    // Log app initialization
    logToCloud('info', 'Housekeeping Web App initialized', 'app.init', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: cloudLogging.getSessionId(),
    });
    
    console.log('📡 CloudLoggingInitializer: Initial app log sent');

    // Log performance metrics
    if (performance && performance.timing) {
      const timing = performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      
      logToCloud('info', `App load time: ${loadTime}ms`, 'app.performance', {
        loadTime,
        domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
        firstPaint: timing.responseEnd - timing.requestStart,
      });
    }

    // Cleanup on unmount
    return () => {
      logToCloud('info', 'App unmounting', 'app.unmount');
      cloudLogging.destroy();
    };
  }, []);

  return <>{children}</>;
};